import React, { FunctionComponent, useState, useEffect } from 'react';

import { chain } from 'lodash';
import { useTranslation } from 'react-i18next';
import { trackPromise } from 'react-promise-tracker';

import {
    DropdownPicker,
    Props as DropdownPickerProps,
    DropDownPickerOverlayArea,
    DropdownDataWithHeader,
} from 'common/dropdown-picker/DropdownPicker';
import {
    getVariableNames,
    cancelGetVariableNames,
    Variable,
    VariableTypeAndObjectID,
    VariableType,
} from 'common/variables/Variables';

import 'common/variables/variable-picker/VariablePicker.scss';

interface Handlers {
    onSelect: any;
    onClose?: any;
    onDataLoad?: any;
}

export interface Props extends Omit<DropdownPickerProps, 'dropdownData' | 'handlers'> {
    agencyId: number;
    tenantId: number;
    dropdownRef?: any;
    variableTypeAndObjectIDs?: VariableTypeAndObjectID[];
    /** will be passed to variables api to get basic variables */
    variableBasic?: boolean;
    /** variables to be removed from the list */
    excludeVariables?: Set<string>;
    handlers?: Handlers;
}

export const VariablePicker: FunctionComponent<Props> = ({
    agencyId,
    tenantId,
    dropdownRef,
    variableTypeAndObjectIDs,
    variableBasic,
    excludeVariables = new Set(),
    handlers,
    searchOptions,
    enableAutoSelect,
    displayOptions,
    autoFocus,
}): JSX.Element => {
    const { t } = useTranslation(['common/variables']);
    const [hasDataLoaded, setHasDataLoaded] = useState(false);
    const [variablesData, setVariablesData] = useState([]);

    useEffect(() => {
        const parseData = (inputData: Variable[]): DropdownDataWithHeader[] => {
            return chain(inputData)
                .remove((variableItem) => {
                    return !excludeVariables!.has(variableItem.variableName);
                })
                .map((variableItem): any => {
                    return {
                        header: t(VariableType[variableItem.variableType]),
                        label: variableItem.variableName.split('-').join(' '),
                        description: `{{${variableItem.variableName}}}`,
                    };
                })
                .sortBy((variableItem) => {
                    if (variableItem.label) {
                        return variableItem.label.toLowerCase();
                    }
                    return '';
                })
                .groupBy((variableItem): any => {
                    return variableItem.header;
                })
                .map((variables, key): any => {
                    return { header: key, content: variables };
                })
                .value();
        };

        const getVariables = async (): Promise<void> => {
            try {
                const data: Variable[] = await trackPromise(
                    getVariableNames(tenantId, variableTypeAndObjectIDs!, variableBasic!),
                    DropDownPickerOverlayArea,
                );
                const parsedData: DropdownDataWithHeader[] = parseData(data);

                setVariablesData(parsedData as any);
                setHasDataLoaded(true);
                if (!autoFocus && handlers && handlers.onDataLoad) {
                    handlers.onDataLoad();
                }
            } catch (error) {
                handleClose();
            }
        };

        getVariables();

        return (): void => {
            cancelGetVariableNames();
        };
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const handleSelection = (value: DropdownDataWithHeader): void => {
        if (handlers!.onSelect) {
            handlers!.onSelect(value);
        }
    };

    const handleClose = (): void => {
        cancelGetVariableNames();
        if (handlers!.onClose) {
            handlers!.onClose();
        }
    };

    return (
        <div className="r4e-variable-picker">
            <DropdownPicker
                ref={dropdownRef}
                searchOptions={searchOptions}
                displayOptions={displayOptions}
                dropdownData={variablesData}
                hasDataLoaded={hasDataLoaded}
                enableAutoSelect={enableAutoSelect}
                hasOverlayPromise
                autoFocus={autoFocus}
                handlers={{
                    onSelect: handleSelection,
                    onOutsideClick: handleClose,
                }}
            />
        </div>
    );
};

VariablePicker.defaultProps = {
    variableTypeAndObjectIDs: [
        {
            variableType: VariableType.SOURCE,
        },
        {
            variableType: VariableType.TENANT,
        },
        {
            variableType: VariableType.LOCATION,
        },
        {
            variableType: VariableType.USER,
        },
    ],
};
